// ============================================
// LISTE DES DOSSIERS — paramètres envoyés à GET /admin/tickets
// ============================================
// La liste est paginée, filtrée et triée par le serveur : le navigateur ne
// garde que la page affichée. Ce module traduit l'état des filtres de l'écran
// en paramètres de requête (contrat : page, limit ≤ 100, status,
// excludeStatus, priority, issueType, assignedToId, search, dateFrom, dateTo,
// sortBy, sortOrder, spamPossible, updatedFrom).
//
// Archives (demande de Nathan du 19/10/2026) : les dossiers fermés (CLOSED)
// sont archivés. La liste « Dossiers » ne les montre plus, « Tous » compris ;
// ils ont leur onglet « Archives » (archives.ts).

import { TicketStatus, type TicketFilters } from '../../types';

/** Filtres de GET /admin/tickets : ceux du type commun, plus « spam possible »
 *  (dossiers nés d'un e-mail jugé indésirable par Microsoft, à trancher). */
export type FiltresApiDossiers = TicketFilters & {
  spamPossible?: boolean;
  /** Dernière mise à jour à partir de ce jour (AAAA-MM-JJ) : période de fermeture des archives. */
  updatedFrom?: string;
};

/** Taille de page par défaut de la liste. */
export const PAR_PAGE = 25;
/** Plafond fixé par le serveur. */
export const LIMITE_MAX = 100;

/** Valeur spéciale du filtre de statut : tout sauf résolu et clos. */
export const A_TRAITER = 'A_TRAITER';

export type TriDossiers = 'createdAt' | 'updatedAt' | 'priority' | 'slaDeadline';
export type SensTri = 'asc' | 'desc';

/** Tris proposés, avec le sens le plus utile pour chacun. */
export const TRIS_DOSSIERS: Array<{ value: TriDossiers; label: string; sens: SensTri }> = [
  { value: 'createdAt', label: 'Date de création', sens: 'desc' },
  { value: 'updatedAt', label: 'Dernière activité', sens: 'desc' },
  // Les priorités sont déclarées LOW → URGENT : l'ordre décroissant met l'urgent en tête.
  { value: 'priority', label: 'Priorité', sens: 'desc' },
  // L'échéance la plus proche d'abord.
  { value: 'slaDeadline', label: 'Échéance SLA', sens: 'asc' },
];

export interface FiltresListe {
  /** `A_TRAITER`, chaîne vide (tous, hors archives) ou un statut précis (jamais « Fermé »). */
  status: string;
  priority: string;
  search: string;
  assignedToId: string;
  dateFrom: string;
  dateTo: string;
  issueType: string;
  /** Seulement les dossiers « spam possible ». Absent : faux (états mémorisés
   *  avant l'ajout du filtre). */
  spamPossible?: boolean;
}

export const FILTRES_PAR_DEFAUT: FiltresListe = {
  status: A_TRAITER, priority: '', search: '', assignedToId: '', dateFrom: '', dateTo: '', issueType: '', spamPossible: false,
};

export function sensParDefaut(tri: TriDossiers): SensTri {
  return TRIS_DOSSIERS.find(t => t.value === tri)?.sens ?? 'desc';
}

/**
 * État de l'écran → filtres de l'API. Les champs vides ne sont pas envoyés ;
 * la recherche est nettoyée ; la limite est bornée à [1, 100].
 */
export function construireFiltresListe(
  filtres: FiltresListe,
  options: { page?: number; limit?: number; tri?: TriDossiers; sens?: SensTri } = {},
): FiltresApiDossiers {
  const tri = options.tri ?? 'createdAt';
  const resultat: FiltresApiDossiers = {
    page: Math.max(1, Math.floor(options.page ?? 1)),
    limit: Math.min(LIMITE_MAX, Math.max(1, Math.floor(options.limit ?? PAR_PAGE))),
    sortBy: tri,
    sortOrder: options.sens ?? sensParDefaut(tri),
  };
  if (filtres.status === A_TRAITER) {
    resultat.excludeStatus = [TicketStatus.RESOLVED, TicketStatus.CLOSED];
  } else if (filtres.status && filtres.status !== TicketStatus.CLOSED) {
    resultat.status = filtres.status as TicketStatus;
  } else {
    // « Tous » : tous les dossiers non archivés. Un filtre « Fermé » resté en
    // mémoire vaut « Tous » : les fermés sont dans l'onglet Archives.
    resultat.excludeStatus = [TicketStatus.CLOSED];
  }
  if (filtres.priority) resultat.priority = filtres.priority as TicketFilters['priority'];
  if (filtres.issueType) resultat.issueType = filtres.issueType as TicketFilters['issueType'];
  if (filtres.assignedToId) resultat.assignedToId = filtres.assignedToId;
  const recherche = filtres.search.trim();
  if (recherche) resultat.search = recherche;
  if (filtres.dateFrom) resultat.dateFrom = filtres.dateFrom;
  if (filtres.dateTo) resultat.dateTo = filtres.dateTo;
  if (filtres.spamPossible) resultat.spamPossible = true;
  return resultat;
}

/** Filtres de l'API → chaîne de requête (sans le « ? »). */
export function parametresRequeteDossiers(filtres: FiltresApiDossiers): string {
  const p = new URLSearchParams();
  if (filtres.page) p.set('page', String(filtres.page));
  if (filtres.limit) p.set('limit', String(Math.min(LIMITE_MAX, Math.max(1, filtres.limit))));
  if (filtres.status) p.set('status', filtres.status);
  else if (filtres.excludeStatus && filtres.excludeStatus.length > 0) p.set('excludeStatus', filtres.excludeStatus.join(','));
  if (filtres.priority) p.set('priority', filtres.priority);
  if (filtres.issueType) p.set('issueType', filtres.issueType);
  if (filtres.assignedToId) p.set('assignedToId', filtres.assignedToId);
  if (filtres.search) p.set('search', filtres.search);
  if (filtres.dateFrom) p.set('dateFrom', filtres.dateFrom);
  if (filtres.dateTo) p.set('dateTo', filtres.dateTo);
  if (filtres.sortBy) p.set('sortBy', filtres.sortBy);
  if (filtres.sortOrder) p.set('sortOrder', filtres.sortOrder);
  if (filtres.spamPossible) p.set('spamPossible', 'true');
  if (filtres.updatedFrom) p.set('updatedFrom', filtres.updatedFrom);
  return p.toString();
}

/** Nombre de filtres actifs affiché à l'écran (« À traiter » est le défaut, il ne compte pas). */
export function compterFiltresActifs(filtres: FiltresListe): number {
  return [
    filtres.status && filtres.status !== A_TRAITER ? filtres.status : '',
    filtres.priority, filtres.assignedToId, filtres.issueType, filtres.dateFrom, filtres.dateTo,
    filtres.spamPossible ? 'spam' : '',
  ].filter(Boolean).length;
}
